import Auth from './auth'

/*
  Recebíveis do repasse automático da Cutilei: o que a Cutilei deve ao salão e à manicure por
  um atendimento de membro já validado.

  Como em `services/Banking`, as chamadas HTTP ficam nas páginas — aqui só mora o que é
  compartilhado entre a aba do salão e as telas do admin.

  Não confundir com `services/Comission`, que calcula a comissão dentro da comanda do salão.
  Este domínio começa depois: a comissão já foi congelada no agendamento, e o que resta é a
  obrigação de transferir.
*/
export default class Payout {
  /*
    Os rótulos são escritos do ponto de vista de quem recebe, e não do estado interno da
    máquina. `failed` é o caso onde isso mais importa: para a API ele quer dizer "não
    conseguimos confirmar se a transferência saiu", e alguém da Cutilei vai conferir o extrato
    no Asaas. Escrever "Falhou" faria o titular concluir que perdeu o dinheiro.
  */
  static STATUS_LABELS = {
    pending: 'A receber',
    processing: 'Em pagamento',
    paid: 'Pago',
    failed: 'Em verificação',
    canceled: 'Cancelado'
  }

  static STATUS_FILTERS = [
    {value: '', label: 'Todos os recebíveis'},
    {value: 'pending', label: 'A receber'},
    {value: 'processing', label: 'Em pagamento'},
    {value: 'paid', label: 'Pagos'},
    {value: 'failed', label: 'Em verificação'},
    {value: 'canceled', label: 'Cancelados'}
  ]

  /*
    As duas pernas de um atendimento. `Professional` é "Manicure" na tela porque é como o
    salão e a própria profissional chamam a função — `Profissional` é o nome da tabela, não
    o do ofício.
  */
  static USER_TYPE_LABELS = {
    Business: 'Salão',
    Professional: 'Manicure'
  }

  static USER_TYPE_FILTERS = [
    {value: '', label: 'Salão e manicure'},
    {value: 'Business', label: 'Salão'},
    {value: 'Professional', label: 'Manicure'}
  ]

  static getStatusLabel(status) {
    return this.STATUS_LABELS[status] ?? 'Situação desconhecida'
  }

  /*
    Mesma paleta de `Banking.getTransactionStatusColor`, e pelo mesmo motivo: verde só no
    desfecho, porque um recebível em pagamento ainda pode voltar para a fila, e pintá-lo de
    verde antes do `paid` faria o titular tratar como recebido um dinheiro que não caiu.

    `failed` fica vermelho junto de `canceled` para aparecer na fila de revisão do admin, que
    é onde ele precisa saltar aos olhos.
  */
  static getStatusColor(status) {
    if (status === 'paid') return '#44CF6C'
    if (['failed', 'canceled'].includes(status)) return '#FF3939'
    return '#FF9800'
  }

  static getUserTypeLabel(userType) {
    return this.USER_TYPE_LABELS[userType] ?? userType
  }

  /*
    Uma linha de recebível como a API a devolve, em camelCase.

    **Este é o único lugar do repositório que conhece os nomes com underscore**, e ele existe
    porque a API responde nas duas convenções no mesmo domínio: `SchedulePayout` não declara
    `serializeAs` nenhum e o Lucid serializa em snake_case, então `GET /payouts`,
    `GET /payouts/:id`, as rotas de antecipação e o razão do admin saem com `gross_amount` e
    `due_at` — enquanto todo resumo e toda cotação são montados à mão no controller e já saem
    em camelCase (`pendingTotal`, `feePercentage`, `nextPayableAt`).

    Normalizar na borda da consulta evita que os dois formatos cheguem a um componente, que é
    onde `payout.dueAt ?? payout.due_at` começaria a se espalhar.

    Campos que a resposta não trouxer ficam `undefined` em vez de `null`: `anticipable` e
    `cancelable` só vêm no `show`, e `duplicated` só na antecipação.
  */
  static normalize(row) {
    if (!row) return null

    return {
      id: row.id,
      scheduleId: row.schedule_id,
      businessId: row.business_id,
      userType: row.user_type,
      userId: row.user_id,
      bankingAccountId: row.banking_account_id,
      scheduleDate: row.schedule_date,
      grossAmount: Number(row.gross_amount ?? 0),
      /* FRAÇÃO de 0 a 1. Ver `toPercentage`, abaixo. */
      commissionPercentage: row.commission_percentage,
      commissionType: row.commission_type,
      amount: Number(row.amount ?? 0),
      /* PERCENTO (5 = 5%), ao contrário do de cima. */
      feePercentage: row.fee_percentage,
      feeAmount: Number(row.fee_amount ?? 0),
      netAmount: Number(row.net_amount ?? 0),
      dueAt: row.due_at,
      payableAt: row.payable_at,
      status: row.status,
      anticipated: Boolean(row.anticipated),
      anticipatedAt: row.anticipated_at,
      bankingTransactionId: row.banking_transaction_id,
      bankingTransaction: row.bankingTransaction ?? null,
      paidAt: row.paid_at,
      lastError: row.last_error,
      needsReview: Boolean(row.needs_review),
      canceledAt: row.canceled_at,
      canceledReason: row.canceled_reason,
      canceledBy: row.canceled_by,
      isTerminal: Boolean(row.is_terminal),
      business: row.business ?? null,
      schedule: row.schedule ?? null,
      anticipable: row.anticipable,
      cancelable: row.cancelable
    }
  }

  static normalizeList(rows = []) {
    return rows.map(row => this.normalize(row))
  }

  /**
   * Converte a fração da comissão no percentual que se lê na tela.
   *
   * Existe para deixar a conversão num lugar só: `commissionPercentage` é fração (0,40) e
   * `feePercentage` é percento (5), e trocar uma pela outra cobra ou mostra cem vezes a mais
   * ou a menos — com um resultado que parece plausível nos dois casos.
   */
  static toPercentage(fraction) {
    if (fraction === null || fraction === undefined) return null

    return Number((Number(fraction) * 100).toFixed(2))
  }

  /*
    O titular é resolvido a partir do token, como em `banking_account`. O administrador é a
    única exceção: ele lê os recebíveis de outro titular apontando para ele na query string —
    e é só leitura, porque as rotas de antecipação recusam `adminAuth` de propósito.

    Devolve objeto, e não uma string com `?`, porque quem chama já está montando query
    (`page`, `per_page`, `status`) e concatenar dois `?` daria uma URL inválida.
  */
  static getOwnerParams() {
    if (!Auth.isAdmin()) return {}

    return { user_type: 'Business', user_id: Auth.getBusinessId() }
  }

  /*
    Antecipar é a única operação desta área que move dinheiro, e movimentar é exclusivo do
    titular. O administrador que entrou no salão continua sendo admin para a API: ele lê a
    lista e não vê botão nenhum, em vez de receber um 403 depois de confirmar.
  */
  static canAnticipate() {
    return !Auth.isAdmin() && ['business', 'professional'].includes(Auth.getUserType())
  }

  static formatMoney(value) {
    return `R$ ${Number(value ?? 0).toFixed(2).replace('.', ',')}`
  }

  /**
   * A conta da antecipação, escrita do mesmo jeito nos dois caminhos que a oferecem.
   *
   * Mora aqui para o lote e o individual não divergirem sobre como a taxa é explicada — e
   * porque o número que o titular precisa ver antes de confirmar é o líquido, não a taxa: é
   * ele que responde "quanto eu recebo se aceitar".
   */
  static getAnticipationMessage({ amount, feePercentage, feeAmount, netAmount, count = 1 }) {
    const what = count > 1
      ? `Antecipar ${count} recebíveis de ${this.formatMoney(amount)}`
      : `Antecipar ${this.formatMoney(amount)}`

    return `${what} com taxa de ${Number(feePercentage ?? 0)}% `
      + `(${this.formatMoney(feeAmount)}).\n\n`
      + `Você recebe ${this.formatMoney(netAmount)} no próximo pagamento, `
      + 'em vez de esperar o vencimento.'
  }
}
